import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost,
  Modal, Field, Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import LineItemsEditor from "@/components/LineItemsEditor";
import { toast } from "sonner";
import { Plus, DownloadSimple, Eye, Printer, ShoppingCart } from "@phosphor-icons/react";
import SmartInput from "@/components/SmartInput";

export default function Quotations() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(null);
  const [skus, setSkus] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer: "", customer_email: "", customer_phone: "", customer_address: "",
    valid_until: "", notes: "",
  });
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(16);
  const canEdit = can(user.role, "staff");

  async function load() {
    try { const { data } = await api.get("/quotations"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const [inv, cust] = await Promise.all([
          api.get("/inventory"),
          api.get("/customers?active_only=true"),
        ]);
        setSkus(inv.data); setCustomers(cust.data);
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, []);

  function openCreate() {
    setForm({ customer: "", customer_email: "", customer_phone: "", customer_address: "", valid_until: "", notes: "" });
    setItems([{ description: "", sku: "", quantity: 1, unit_price: 0 }]);
    setTax(16);
    setCreateOpen(true);
  }
  async function submit(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          description: i.description, sku: i.sku || null,
          quantity: Number(i.quantity), unit_price: Number(i.unit_price),
        })),
        tax_rate: Number(tax),
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      };
      await api.post("/quotations", payload);
      toast.success("Quotation created");
      setCreateOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function setStatus(q, status) {
    try {
      await api.patch(`/quotations/${q.id}/status?status=${status}`);
      toast.success(`Marked ${status}`);
      setViewOpen(null); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function convert(q) {
    if (!window.confirm(`Convert ${q.number} to a sales order?`)) return;
    try {
      await api.post(`/quotations/${q.id}/convert`);
      toast.success("Quotation converted to order");
      setViewOpen(null); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="quote-page">
      <PageHeader
        overline="Module / Sales"
        title="Quotations"
        subtitle="Generate quotes, send to customers, print PDF, convert to orders on acceptance."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `quotations-${Date.now()}.csv`)} data-testid="quote-export">
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="quote-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> New Quote
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No quotations yet." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr><th>Number</th><th>Customer</th><th>Created</th><th>Valid Until</th>
                  <th className="text-right">Total</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.number}`}>
                    <td className="mono font-semibold">{q.number}</td>
                    <td>{q.customer}</td>
                    <td className="text-sm text-[#52525b]">{fmtDate(q.created_at)}</td>
                    <td className="text-sm text-[#52525b]">{fmtDate(q.valid_until)}</td>
                    <td className="text-right mono font-semibold">{fmtZMW(q.total)}</td>
                    <td><StatusBadge status={q.status} /></td>
                    <td className="text-right whitespace-nowrap">
                      <BtnGhost onClick={() => setViewOpen(q)} data-testid={`quote-view-${q.number}`}>
                        <Eye size={12} weight="bold" /> View
                      </BtnGhost>
                      <Link to={`/quotations/${q.id}/print`} target="_blank" rel="noreferrer">
                        <BtnGhost data-testid={`quote-print-${q.number}`}>
                          <Printer size={12} weight="bold" /> Print
                        </BtnGhost>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)}
        title="New Quotation" wide testId="quote-create-modal">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer Name" hint="Pick from saved customers or type a new one">
              <SmartInput
                required value={form.customer}
                testId="q-customer"
                onChange={(v) => {
                  // Auto-fill address/email if matching saved customer
                  const match = customers.find((c) => c.name === v);
                  if (match) {
                    setForm({
                      ...form, customer: v,
                      customer_email: match.email || form.customer_email,
                      customer_phone: match.phone || form.customer_phone,
                      customer_address: match.address || form.customer_address,
                    });
                  } else {
                    setForm({ ...form, customer: v });
                  }
                }}
                options={customers.map((c) => ({ value: c.name, hint: c.code }))}
              />
            </Field>
            <Field label="Email">
              <Input type="email"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
            </Field>
            <Field label="Phone">
              <Input value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
            </Field>
            <Field label="Valid Until">
              <Input type="date"
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </Field>
          </div>
          <Field label="Billing Address">
            <Textarea value={form.customer_address}
              onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
          </Field>
          <LineItemsEditor
            items={items} setItems={setItems}
            taxRate={tax} setTaxRate={setTax}
            skuOptions={skus}
          />
          <Field label="Notes">
            <Textarea value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setCreateOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="q-submit">Create Quotation</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* View modal */}
      <Modal open={!!viewOpen} onClose={() => setViewOpen(null)}
        title={viewOpen ? `Quotation ${viewOpen.number}` : ""} wide testId="quote-view-modal">
        {viewOpen && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="phizo-label">Customer</div><div className="font-semibold">{viewOpen.customer}</div></div>
              <div><div className="phizo-label">Status</div><StatusBadge status={viewOpen.status} /></div>
              <div><div className="phizo-label">Total</div><div className="font-heading text-xl font-black">{fmtZMW(viewOpen.total)}</div></div>
              <div><div className="phizo-label">Valid Until</div><div>{fmtDate(viewOpen.valid_until)}</div></div>
            </div>

            <div className="flex flex-wrap gap-2 pt-3 border-t border-[#e4e4e7]">
              <Link to={`/quotations/${viewOpen.id}/print`} target="_blank" rel="noreferrer">
                <BtnSecondary>
                  <Printer size={14} weight="bold" className="inline mr-1" /> Print / PDF
                </BtnSecondary>
              </Link>
              {canEdit && viewOpen.status === "draft" && (
                <BtnSecondary onClick={() => setStatus(viewOpen, "sent")}>Mark Sent</BtnSecondary>
              )}
              {canEdit && ["draft", "sent"].includes(viewOpen.status) && (
                <>
                  <BtnSecondary onClick={() => setStatus(viewOpen, "accepted")}>Mark Accepted</BtnSecondary>
                  <BtnSecondary onClick={() => setStatus(viewOpen, "rejected")}>Mark Rejected</BtnSecondary>
                </>
              )}
              {canEdit && ["draft", "sent", "accepted"].includes(viewOpen.status) && (
                <BtnPrimary onClick={() => convert(viewOpen)} data-testid="q-convert">
                  <ShoppingCart size={14} weight="bold" className="inline mr-1" /> Convert to Order
                </BtnPrimary>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
